import type { BoardCell, WarchestState } from "../types/game-state";
import {
  areAdjacent,
  getDirectionVector,
  getHexDistance,
  getHexInDirection,
  isLocation,
} from "../utils/hex-utils";
import { registerTactic } from "../utils/tactic-registry";
import {
  advanceActivePlayer,
  allHandsEmpty,
  moveCoin,
} from "../utils/zone-utils";

// ── shared helpers ────────────────────────────────────────────────────────────

/** Remove one stack coin from a unit. Returns the new board with the cell updated/removed
 *  and also adds the unitType to the enemy's removed pile if destroyed. */
function applyStackDamage(
  state: WarchestState,
  targetHex: string,
): WarchestState {
  const targetCell = state.board[targetHex] as BoardCell;
  const targetOwner = targetCell.owner;
  const newStackSize = targetCell.stackSize - 1;
  const newBoard = {
    ...state.board,
    [targetHex]:
      newStackSize > 0 ? { ...targetCell, stackSize: newStackSize } : null,
  };
  const targetZones = state.playerZones[targetOwner];
  const newTargetZones =
    newStackSize <= 0
      ? {
          ...targetZones,
          removed: [...targetZones.removed, targetCell.unitType],
        }
      : targetZones;
  return {
    ...state,
    board: newBoard,
    playerZones: { ...state.playerZones, [targetOwner]: newTargetZones },
  };
}

// Used only by ROYAL_GUARD_PROTECT, which is returned directly from apply-tactic.ts
// special-case branch (bypassing the outer turn-advance code).
function advanceTurn(state: WarchestState, player: string): WarchestState {
  const newPlayerZones = state.playerZones;
  const nextActive = advanceActivePlayer(player as "P1" | "P2", newPlayerZones);
  const nextPhase = allHandsEmpty(newPlayerZones)
    ? ("DRAW_COINS" as const)
    : state.phase;
  return {
    ...state,
    activePlayer: nextActive,
    phase: nextPhase,
    initiativeTransferredThisRound:
      nextPhase === "DRAW_COINS" ? false : state.initiativeTransferredThisRound,
    turn: nextPhase === "DRAW_COINS" ? state.turn + 1 : state.turn,
  };
}

/** Move a unit from one hex to another. */
function moveUnit(
  state: WarchestState,
  fromHex: string,
  toHex: string,
): WarchestState {
  const cell = state.board[fromHex];
  if (!cell) return state;
  return {
    ...state,
    board: { ...state.board, [fromHex]: null, [toHex]: cell },
  };
}

// ── ARCHER_RANGED ─────────────────────────────────────────────────────────────
registerTactic("ARCHER_RANGED", {
  validate(state, action) {
    const boardDef = state.config.boards[state.boardId];
    if (!boardDef) return { valid: false, error: "Board not found" };
    const { targetHex } = action.tacticPayload as { targetHex: string };
    if (!targetHex) return { valid: false, error: "Missing targetHex" };
    const dist = getHexDistance(boardDef, action.unitHex, targetHex);
    if (dist !== 2)
      return { valid: false, error: "Archer must target exactly 2 hexes away" };
    const targetCell = state.board[targetHex];
    if (!targetCell) return { valid: false, error: "No unit at target hex" };
    if (targetCell.owner === action.player)
      return { valid: false, error: "Cannot target friendly unit" };
    return { valid: true };
  },
  apply(state, action) {
    const { targetHex } = action.tacticPayload as { targetHex: string };
    // Coin already discarded by applyTactic; just apply the effect.
    // Turn advance is handled by the outer applyTactic wrapper.
    return applyStackDamage(state, targetHex);
  },
});

// ── BERSERKER_CHARGE ─────────────────────────────────────────────────────────
registerTactic("BERSERKER_CHARGE", {
  validate(state, action) {
    const boardDef = state.config.boards[state.boardId];
    if (!boardDef) return { valid: false, error: "Board not found" };
    const { moves } = action.tacticPayload as { moves: readonly string[] };
    if (!moves || moves.length === 0)
      return { valid: false, error: "At least 1 move required" };
    const attackerCell = state.board[action.unitHex];
    if (!attackerCell) return { valid: false, error: "No unit at unitHex" };
    // Each chain move costs 1 stack coin; first move is free
    const chainCost = moves.length - 1;
    if (chainCost > attackerCell.stackSize) {
      return { valid: false, error: "Not enough stack coins for chain moves" };
    }
    // Validate each step is adjacent to the previous position
    let prev = action.unitHex;
    for (const step of moves) {
      if (!areAdjacent(boardDef, prev, step)) {
        return { valid: false, error: `Non-adjacent step: ${prev} → ${step}` };
      }
      if (state.board[step] !== null && state.board[step] !== undefined) {
        return { valid: false, error: `Path hex ${step} is occupied` };
      }
      prev = step;
    }
    return { valid: true };
  },
  apply(state, action) {
    const { moves } = action.tacticPayload as { moves: readonly string[] };
    const attackerCell = state.board[action.unitHex] as BoardCell;
    const finalHex = moves[moves.length - 1] as string;
    const chainCost = moves.length - 1;
    const newStackSize = attackerCell.stackSize - chainCost;
    const newBoard = { ...state.board, [action.unitHex]: null };
    if (newStackSize > 0) {
      newBoard[finalHex] = { ...attackerCell, stackSize: newStackSize };
    } else {
      newBoard[finalHex] = null;
    }
    let newPlayerZones = state.playerZones;
    if (newStackSize <= 0) {
      const pz = newPlayerZones[action.player];
      newPlayerZones = {
        ...newPlayerZones,
        [action.player]: {
          ...pz,
          removed: [...pz.removed, attackerCell.unitType],
        },
      };
    }
    return { ...state, board: newBoard, playerZones: newPlayerZones };
  },
});

// ── CAVALRY_MANEUVER ─────────────────────────────────────────────────────────
registerTactic("CAVALRY_MANEUVER", {
  validate(state, action) {
    const boardDef = state.config.boards[state.boardId];
    if (!boardDef) return { valid: false, error: "Board not found" };
    const { toHex, attackHex } = action.tacticPayload as {
      toHex: string;
      attackHex: string;
    };
    if (!toHex || !attackHex)
      return { valid: false, error: "Missing toHex or attackHex" };
    if (!areAdjacent(boardDef, action.unitHex, toHex)) {
      return { valid: false, error: "toHex must be adjacent to unitHex" };
    }
    if (state.board[toHex])
      return { valid: false, error: "toHex must be empty" };
    if (!areAdjacent(boardDef, toHex, attackHex)) {
      return { valid: false, error: "attackHex must be adjacent to toHex" };
    }
    const attackTarget = state.board[attackHex];
    if (!attackTarget) return { valid: false, error: "No unit at attackHex" };
    if (attackTarget.owner === action.player)
      return { valid: false, error: "Cannot attack friendly unit" };
    return { valid: true };
  },
  apply(state, action) {
    const { toHex, attackHex } = action.tacticPayload as {
      toHex: string;
      attackHex: string;
    };
    let next = moveUnit(state, action.unitHex, toHex);
    next = applyStackDamage(next, attackHex);
    return next;
  },
});

// ── CROSSBOWMAN_BOLT ─────────────────────────────────────────────────────────
registerTactic("CROSSBOWMAN_BOLT", {
  validate(state, action) {
    const boardDef = state.config.boards[state.boardId];
    if (!boardDef) return { valid: false, error: "Board not found" };
    const { targetHex } = action.tacticPayload as { targetHex: string };
    if (!targetHex) return { valid: false, error: "Missing targetHex" };
    // Determine direction: find dir from unitHex such that stepping twice reaches targetHex
    const unitNeighbors = boardDef.adjacency[action.unitHex] ?? [];
    let intermediateHex: string | null = null;
    for (const nb of unitNeighbors) {
      const dir = getDirectionVector(action.unitHex, nb);
      if (!dir) continue;
      const twoStepHex = getHexInDirection(action.unitHex, dir, 2);
      if (twoStepHex === targetHex) {
        intermediateHex = nb;
        break;
      }
    }
    if (!intermediateHex) {
      return {
        valid: false,
        error:
          "Target must be exactly 2 steps in a straight line from crossbowman",
      };
    }
    // Intermediate must be empty
    const intermediate = state.board[intermediateHex];
    if (intermediate !== null && intermediate !== undefined) {
      return { valid: false, error: "Intermediate hex must be empty" };
    }
    // Target must have an enemy
    const targetCell = state.board[targetHex];
    if (!targetCell) return { valid: false, error: "No unit at target hex" };
    if (targetCell.owner === action.player)
      return { valid: false, error: "Cannot target friendly unit" };
    return { valid: true };
  },
  apply(state, action) {
    const { targetHex } = action.tacticPayload as { targetHex: string };
    return applyStackDamage(state, targetHex);
  },
});

// ── ENSIGN_RALLY ─────────────────────────────────────────────────────────────
registerTactic("ENSIGN_RALLY", {
  validate(state, action) {
    const boardDef = state.config.boards[state.boardId];
    if (!boardDef) return { valid: false, error: "Board not found" };
    const { friendlyHex, toHex } = action.tacticPayload as {
      friendlyHex: string;
      toHex: string;
    };
    if (!friendlyHex || !toHex)
      return { valid: false, error: "Missing friendlyHex or toHex" };
    // friendlyHex must have a friendly unit
    const friendlyCell = state.board[friendlyHex];
    if (!friendlyCell) return { valid: false, error: "No unit at friendlyHex" };
    if (friendlyCell.owner !== action.player)
      return { valid: false, error: "Unit at friendlyHex is not yours" };
    // friendlyHex within range 2 of ensign
    const distToFriendly = getHexDistance(
      boardDef,
      action.unitHex,
      friendlyHex,
    );
    if (distToFriendly > 2)
      return {
        valid: false,
        error: "Friendly unit is out of Ensign's range (2)",
      };
    // toHex must be adjacent to friendlyHex
    if (!areAdjacent(boardDef, friendlyHex, toHex)) {
      return { valid: false, error: "toHex must be adjacent to friendlyHex" };
    }
    // toHex must be empty
    if (state.board[toHex])
      return { valid: false, error: "toHex must be empty" };
    return { valid: true };
  },
  apply(state, action) {
    const { friendlyHex, toHex } = action.tacticPayload as {
      friendlyHex: string;
      toHex: string;
    };
    return moveUnit(state, friendlyHex, toHex);
  },
});

// ── FOOTMAN_FORMATION ────────────────────────────────────────────────────────

interface FootmanManeuver {
  readonly footmanHex: string;
  readonly action: "MOVE" | "ATTACK" | "CONTROL";
  readonly targetHex: string;
}

registerTactic("FOOTMAN_FORMATION", {
  validate(state, action) {
    const boardDef = state.config.boards[state.boardId];
    if (!boardDef) return { valid: false, error: "Board not found" };
    const { maneuvers } = action.tacticPayload as {
      maneuvers: readonly FootmanManeuver[];
    };
    if (!maneuvers || maneuvers.length === 0) {
      return { valid: false, error: "At least 1 maneuver required" };
    }
    for (const m of maneuvers) {
      const cell = state.board[m.footmanHex];
      if (!cell) return { valid: false, error: `No unit at ${m.footmanHex}` };
      if (cell.owner !== action.player)
        return { valid: false, error: `Unit at ${m.footmanHex} is not yours` };
      if (cell.unitType !== "FOOTMAN")
        return {
          valid: false,
          error: `Unit at ${m.footmanHex} is not a FOOTMAN`,
        };
      if (!areAdjacent(boardDef, m.footmanHex, m.targetHex)) {
        return {
          valid: false,
          error: `targetHex must be adjacent to footmanHex`,
        };
      }
      if (m.action === "MOVE") {
        if (state.board[m.targetHex])
          return { valid: false, error: `targetHex must be empty for MOVE` };
      } else if (m.action === "ATTACK") {
        const target = state.board[m.targetHex];
        if (!target)
          return { valid: false, error: `No enemy at targetHex for ATTACK` };
        if (target.owner === action.player)
          return { valid: false, error: `Cannot attack friendly unit` };
      } else if (m.action === "CONTROL") {
        if (m.targetHex !== m.footmanHex)
          return {
            valid: false,
            error: `CONTROL targetHex must equal footmanHex`,
          };
        if (!isLocation(boardDef, m.footmanHex))
          return { valid: false, error: `footmanHex is not a location` };
      }
    }
    return { valid: true };
  },
  apply(state, action) {
    const { maneuvers } = action.tacticPayload as {
      maneuvers: readonly FootmanManeuver[];
    };
    let next = state;
    for (const m of maneuvers) {
      if (m.action === "MOVE") {
        next = moveUnit(next, m.footmanHex, m.targetHex);
      } else if (m.action === "ATTACK") {
        next = applyStackDamage(next, m.targetHex);
      } else if (m.action === "CONTROL") {
        // Control: update location ownership
        next = {
          ...next,
          locations: {
            ...next.locations,
            [m.footmanHex]: { controlledBy: action.player as "P1" | "P2" },
          },
        };
      }
    }
    return next;
  },
});

// ── LANCER_THRUST ─────────────────────────────────────────────────────────────
registerTactic("LANCER_THRUST", {
  validate(state, action) {
    const boardDef = state.config.boards[state.boardId];
    if (!boardDef) return { valid: false, error: "Board not found" };
    const { path, attackHex } = action.tacticPayload as {
      path: readonly string[];
      attackHex: string;
    };
    if (!path || path.length === 0 || path.length > 2) {
      return { valid: false, error: "Path must have 1 or 2 hexes" };
    }
    if (!attackHex) return { valid: false, error: "Missing attackHex" };
    // Determine direction from unitHex to path[0]
    const dir = getDirectionVector(action.unitHex, path[0] as string);
    if (!dir)
      return { valid: false, error: "First path step is not adjacent to unit" };
    // Each step in path must be in the same direction from previous
    let prev = action.unitHex;
    for (let i = 0; i < path.length; i++) {
      const step = path[i] as string;
      const expected = getHexInDirection(prev, dir, 1);
      if (expected !== step)
        return {
          valid: false,
          error: `Path step ${i} is not in the same direction`,
        };
      if (!boardDef.hexes.includes(step))
        return { valid: false, error: `Hex ${step} not on board` };
      if (state.board[step])
        return { valid: false, error: `Path hex ${step} is occupied` };
      prev = step;
    }
    // attackHex must be the next hex in the same direction from path[-1]
    const lastHex = path[path.length - 1] as string;
    const expectedAttack = getHexInDirection(lastHex, dir, 1);
    if (expectedAttack !== attackHex) {
      return {
        valid: false,
        error: "attackHex must be in the same direction as the path",
      };
    }
    // attackHex must have an enemy
    const attackTarget = state.board[attackHex];
    if (!attackTarget) return { valid: false, error: "No unit at attackHex" };
    if (attackTarget.owner === action.player)
      return { valid: false, error: "Cannot attack friendly unit" };
    return { valid: true };
  },
  apply(state, action) {
    const { path, attackHex } = action.tacticPayload as {
      path: readonly string[];
      attackHex: string;
    };
    const finalHex = path[path.length - 1] as string;
    let next = moveUnit(state, action.unitHex, finalHex);
    next = applyStackDamage(next, attackHex);
    return next;
  },
});

// ── LIGHT_CAVALRY_SWIFT ──────────────────────────────────────────────────────
registerTactic("LIGHT_CAVALRY_SWIFT", {
  validate(state, action) {
    const boardDef = state.config.boards[state.boardId];
    if (!boardDef) return { valid: false, error: "Board not found" };
    const { path } = action.tacticPayload as { path: readonly string[] };
    if (!path || path.length !== 2) {
      return { valid: false, error: "Path must be exactly 2 hexes" };
    }
    const [step1, step2] = path as [string, string];
    // step1 must be adjacent to unitHex
    if (!areAdjacent(boardDef, action.unitHex, step1)) {
      return { valid: false, error: "First step must be adjacent to unit" };
    }
    // step1 must be empty
    if (state.board[step1])
      return { valid: false, error: "First step must be empty" };
    // step2 must be adjacent to step1
    if (!areAdjacent(boardDef, step1, step2)) {
      return {
        valid: false,
        error: "Second step must be adjacent to first step",
      };
    }
    // step2 must be empty
    if (state.board[step2])
      return { valid: false, error: "Destination must be empty" };
    return { valid: true };
  },
  apply(state, action) {
    const { path } = action.tacticPayload as { path: readonly string[] };
    const dest = path[path.length - 1] as string;
    return moveUnit(state, action.unitHex, dest);
  },
});

// ── MARSHALL_COMMAND ─────────────────────────────────────────────────────────
registerTactic("MARSHALL_COMMAND", {
  validate(state, action) {
    const boardDef = state.config.boards[state.boardId];
    if (!boardDef) return { valid: false, error: "Board not found" };
    const { friendlyHex, targetHex } = action.tacticPayload as {
      friendlyHex: string;
      targetHex: string;
    };
    if (!friendlyHex || !targetHex)
      return { valid: false, error: "Missing friendlyHex or targetHex" };
    // friendlyHex must be adjacent to marshall (unitHex)
    if (!areAdjacent(boardDef, action.unitHex, friendlyHex)) {
      return {
        valid: false,
        error: "friendlyHex must be adjacent to Marshall",
      };
    }
    // friendlyHex must have a friendly unit that can normally attack
    const friendlyCell = state.board[friendlyHex];
    if (!friendlyCell) return { valid: false, error: "No unit at friendlyHex" };
    if (friendlyCell.owner !== action.player)
      return { valid: false, error: "Unit at friendlyHex is not yours" };
    const friendlyUnitDef = state.config.units[friendlyCell.unitType];
    if (!friendlyUnitDef?.normalAttackAllowed) {
      return { valid: false, error: "Commanded unit cannot normally attack" };
    }
    // targetHex must be adjacent to friendlyHex
    if (!areAdjacent(boardDef, friendlyHex, targetHex)) {
      return {
        valid: false,
        error: "targetHex must be adjacent to friendlyHex",
      };
    }
    // targetHex must have an enemy
    const targetCell = state.board[targetHex];
    if (!targetCell) return { valid: false, error: "No unit at targetHex" };
    if (targetCell.owner === action.player)
      return { valid: false, error: "Cannot attack friendly unit" };
    return { valid: true };
  },
  apply(state, action) {
    const { targetHex } = action.tacticPayload as { targetHex: string };
    // Friendly unit doesn't move — only the enemy is damaged
    return applyStackDamage(state, targetHex);
  },
});

// ── ROYAL_GUARD_PROTECT ──────────────────────────────────────────────────────
// Note: coinType is "ROYAL"; routing is done via validate-tactic.ts special case.
// apply-tactic.ts returns this handler's result directly (bypassing the outer
// turn-advance code), so this handler must handle turn advance itself.
registerTactic("ROYAL_GUARD_PROTECT", {
  validate(state, action) {
    const boardDef = state.config.boards[state.boardId];
    if (!boardDef) return { valid: false, error: "Board not found" };
    const { toHex } = action.tacticPayload as { toHex: string };
    if (!toHex) return { valid: false, error: "Missing toHex" };
    // toHex must be adjacent to royal guard (unitHex)
    if (!areAdjacent(boardDef, action.unitHex, toHex)) {
      return { valid: false, error: "toHex must be adjacent to Royal Guard" };
    }
    // toHex must be a location controlled by the player
    if (!isLocation(boardDef, toHex)) {
      return { valid: false, error: "toHex must be a board location" };
    }
    const locState = state.locations[toHex];
    if (!locState || locState.controlledBy !== action.player) {
      return { valid: false, error: "toHex must be controlled by you" };
    }
    // toHex must be empty
    if (state.board[toHex])
      return { valid: false, error: "toHex must be empty" };
    return { valid: true };
  },
  apply(state, action) {
    const { toHex } = action.tacticPayload as { toHex: string };
    // Discard the ROYAL coin — apply-tactic.ts special-case branch returns this
    // handler's result directly, so coin discard and turn advance happen here.
    const zones = moveCoin(
      state.playerZones[action.player],
      "ROYAL",
      "hand",
      "discardFaceUp",
    );
    let next: WarchestState = {
      ...state,
      playerZones: { ...state.playerZones, [action.player]: zones },
    };
    next = moveUnit(next, action.unitHex, toHex);
    return advanceTurn(next, action.player);
  },
});
